// Расшифровка запуска агента: текст, вызовы инструментов, результаты, ошибки. Живое дополнение через SSE.
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { api } from '../api.ts'
import { useAsync } from '../lib/hooks.ts'
import { formatTime, formatUsd, truncate } from '../lib/format.ts'
import { Markdown } from '../lib/markdown.tsx'
import { useReconnect, useStream } from '../store.ts'
import type { JobDetail, JobEvent } from '../types.ts'
import { Icon } from './Icon.tsx'
import { Collapsible, ErrorBox, Loading, LiveDot, cx } from './ui.tsx'

function str(v: unknown): string {
  if (typeof v === 'string') return v
  if (v === null || v === undefined) return ''
  try {
    return JSON.stringify(v, null, 2)
  } catch {
    return String(v)
  }
}

export function outputText(out: unknown): string {
  if (typeof out === 'string') return out
  if (Array.isArray(out)) {
    return out
      .map((b) => {
        if (typeof b === 'string') return b
        if (b && typeof b === 'object' && 'text' in b && typeof (b as { text: unknown }).text === 'string') {
          return (b as { text: string }).text
        }
        if (b && typeof b === 'object' && 'type' in b && (b as { type: unknown }).type === 'image') return '[изображение]'
        return str(b)
      })
      .join('\n')
  }
  return str(out)
}

export function toolName(name: string): string {
  const m = /^mcp__([^_]+(?:_[^_]+)*?)__(.+)$/.exec(name)
  return m ? `${m[1]} · ${m[2]}` : name
}

const INPUT_KEYS = ['command', 'file_path', 'path', 'pattern', 'url', 'query', 'q', 'question', 'subject', 'title', 'description', 'prompt', 'service_id', 'task_id']

export function summarizeInput(input: unknown): string {
  if (input === null || input === undefined) return ''
  if (typeof input !== 'object') return String(input)
  const o = input as Record<string, unknown>
  for (const k of INPUT_KEYS) {
    const v = o[k]
    if ((typeof v === 'string' && v) || typeof v === 'number') return k === 'command' ? `$ ${String(v)}` : String(v)
  }
  try {
    return JSON.stringify(o)
  } catch {
    return ''
  }
}

function ToolUse({ data }: { data: Record<string, unknown> }) {
  const name = typeof data.name === 'string' ? data.name : 'tool'
  const input = data.input
  const summary = summarizeInput(input)
  const [open, setOpen] = useState(false)
  const full = str(input)
  const expandable = full.length > 0 && (full.length > 140 || full !== summary)
  return (
    <div className="rounded-ctl border border-line bg-surface-2 px-2.5 py-1.5">
      <button
        type="button"
        className={cx('flex w-full min-w-0 items-center gap-1.5 text-left', !expandable && 'cursor-default')}
        onClick={() => expandable && setOpen((v) => !v)}
        aria-expanded={expandable ? open : undefined}
      >
        <Icon name="tool" size={13} className="shrink-0 text-muted" />
        {/* имя MCP-инструмента бывает длинным (uuid сервера) — обрезаем, чтобы строка не вылезала за край */}
        <span className="max-w-[60%] shrink-0 truncate font-mono text-[12px] font-semibold text-fg-2" title={name}>
          {toolName(name)}
        </span>
        <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-muted">{truncate(summary.replace(/\s+/g, ' '), 220)}</span>
        {expandable ? (
          <Icon name="chevronRight" size={13} className={cx('shrink-0 text-subtle transition-transform', open && 'rotate-90')} />
        ) : null}
      </button>
      {open ? (
        <pre className="scroll-thin mt-1.5 max-h-72 overflow-auto rounded-ctl border border-line bg-surface p-2.5 font-mono text-[12px] leading-relaxed whitespace-pre-wrap text-fg-2 [overflow-wrap:anywhere]">
          {truncate(full, 20000)}
        </pre>
      ) : null}
    </div>
  )
}

function ToolResult({ data }: { data: Record<string, unknown> }) {
  const text = outputText(data.output ?? data.content ?? data.result)
  const isError = Boolean(data.is_error)
  const lines = text ? text.split('\n') : []
  const first = lines.find((l) => l.trim()) ?? ''
  const [open, setOpen] = useState(false)
  return (
    <div className={cx('ml-4 rounded-ctl border px-2.5 py-1', isError ? 'border-transparent bg-danger-soft' : 'border-line bg-surface')}>
      <button
        type="button"
        className="flex w-full min-w-0 items-center gap-1.5 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Icon name="chevronRight" size={12} className={cx('shrink-0 text-subtle transition-transform', open && 'rotate-90')} />
        <span className={cx('shrink-0 text-[11px] font-medium', isError ? 'text-danger' : 'text-subtle')}>
          {isError ? 'ошибка инструмента' : 'результат'}
        </span>
        <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-muted">{truncate(first, 200) || '(пусто)'}</span>
        {lines.length > 1 ? <span className="tnum shrink-0 text-[11px] text-subtle">{lines.length} стр.</span> : null}
      </button>
      {open ? (
        <pre className="scroll-thin mt-1 mb-1 max-h-96 overflow-auto rounded-ctl border border-line bg-surface-2 p-2.5 font-mono text-[12px] leading-relaxed whitespace-pre-wrap text-fg-2 [overflow-wrap:anywhere]">
          {truncate(text, 30000)}
        </pre>
      ) : null}
    </div>
  )
}

function EventRow({ ev }: { ev: JobEvent }) {
  const d = ev.data ?? {}
  switch (ev.type) {
    case 'text':
      return (
        <div className="flex gap-2">
          <Icon name="message" size={14} className="mt-0.5 shrink-0 text-subtle" />
          <Markdown text={str(d.text)} className="min-w-0 flex-1 text-[13.5px]" />
        </div>
      )
    case 'thinking':
      return (
        <Collapsible summary={<span className="italic">размышления</span>}>
          <div className="border-l-2 border-line pl-2 text-[12.5px] whitespace-pre-wrap text-muted italic [overflow-wrap:anywhere]">
            {str(d.text ?? d.thinking)}
          </div>
        </Collapsible>
      )
    case 'tool_use':
      return <ToolUse data={d} />
    case 'tool_result':
      return <ToolResult data={d} />
    case 'error':
      return (
        <div className="flex items-start gap-2 rounded-ctl bg-danger-soft px-2.5 py-1.5 text-[12.5px] text-danger">
          <Icon name="alertCircle" size={14} className="mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1 font-mono whitespace-pre-wrap [overflow-wrap:anywhere]">{str(d.message ?? d.error ?? d)}</div>
        </div>
      )
    case 'result':
      return (
        <div className="rounded-ctl border border-line bg-surface px-3 py-2.5 shadow-[inset_3px_0_0_var(--orbis-good)]">
          <div className="mb-1 flex flex-wrap items-center gap-1.5 text-[12px] font-semibold text-ok">
            <Icon name="check" size={13} />
            Итог запуска
            {typeof d.cost_usd === 'number' ? <span className="tnum font-normal text-muted">· {formatUsd(d.cost_usd)}</span> : null}
            {typeof d.num_turns === 'number' ? <span className="tnum font-normal text-muted">· {d.num_turns} ходов</span> : null}
          </div>
          <Markdown text={str(d.result ?? d.text ?? '')} className="text-[13.5px]" />
        </div>
      )
    case 'system':
    default: {
      const label = typeof d.subtype === 'string' ? d.subtype : typeof d.message === 'string' ? d.message : str(d)
      return (
        <div className="truncate text-[11.5px] text-subtle" title={str(d)}>
          · {truncate(label, 200)}
        </div>
      )
    }
  }
}

export function JobTranscript({ jobId }: { jobId: number }) {
  const { data, error, loading, reload, setData } = useAsync(
    (signal) => api.get<JobDetail>(`/api/jobs/${jobId}`, { signal }),
    [jobId],
  )
  const scroller = useRef<HTMLDivElement>(null)
  const stick = useRef(true)

  useStream((ev) => {
    if (ev.type === 'job_event' && ev.data.job_id === jobId) {
      setData((cur) => {
        if (!cur || cur.events.some((x) => x.id === ev.data.id)) return cur
        const last = cur.events[cur.events.length - 1]
        const events = [...cur.events, ev.data]
        if (last && last.seq > ev.data.seq) events.sort((a, b) => a.seq - b.seq)
        return { ...cur, events }
      })
    } else if (ev.type === 'job' && ev.data.id === jobId) {
      setData((cur) => (cur ? { ...cur, job: { ...cur.job, ...ev.data } } : cur))
    }
  })
  useReconnect(reload)

  const count = data?.events.length ?? 0
  useLayoutEffect(() => {
    const el = scroller.current
    if (el && stick.current) el.scrollTop = el.scrollHeight
  }, [count])

  useEffect(() => {
    stick.current = true
  }, [jobId])

  if (loading && !data) return <Loading label="Загружаю расшифровку…" />
  if (error && !data) return <ErrorBox error={error} onRetry={reload} />
  if (!data) return null
  const { job, events } = data
  const live = job.status === 'running'
  const hasResultEvent = events.some((e) => e.type === 'result')

  return (
    <div className="flex flex-col gap-2">
      {job.prompt ? (
        <Collapsible summary="Задание агенту (prompt)">
          <pre className="scroll-thin max-h-72 overflow-auto rounded-ctl border border-line bg-surface-2 p-2.5 font-mono text-[12px] leading-relaxed whitespace-pre-wrap text-fg-2 [overflow-wrap:anywhere]">
            {job.prompt}
          </pre>
        </Collapsible>
      ) : null}
      <div
        ref={scroller}
        onScroll={(e) => {
          const el = e.currentTarget
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
        }}
        className="scroll-thin flex max-h-[60vh] min-w-0 flex-col gap-1.5 overflow-y-auto pr-1"
      >
        {events.length === 0 ? (
          <div className="py-3 text-[12.5px] text-muted">
            {job.status === 'queued' ? 'Запуск в очереди — ждёт свободного раннера.' : 'Событий пока нет.'}
          </div>
        ) : (
          events.map((ev) => (
            <div key={ev.id} className="group/ev relative">
              <EventRow ev={ev} />
              <span className="tnum pointer-events-none absolute top-0.5 -left-12 hidden w-10 text-right text-[10px] text-subtle xl:group-hover/ev:block">
                {formatTime(ev.created_at)}
              </span>
            </div>
          ))
        )}
        {live ? (
          <div className="flex items-center gap-2 py-1 text-[12px] text-ok">
            <LiveDot /> агент работает…
          </div>
        ) : null}
      </div>
      {!hasResultEvent && job.result ? (
        <div className="rounded-ctl border border-line bg-surface px-3 py-2.5 shadow-[inset_3px_0_0_var(--orbis-good)]">
          <div className="mb-1 text-[12px] font-semibold text-ok">Итог запуска</div>
          <Markdown text={job.result} className="text-[13.5px]" />
        </div>
      ) : null}
      {job.error ? (
        <div className="flex items-start gap-2 rounded-ctl bg-danger-soft px-2.5 py-1.5 text-[12.5px] text-danger">
          <Icon name="alertCircle" size={14} className="mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1 font-mono whitespace-pre-wrap [overflow-wrap:anywhere]">{job.error}</div>
        </div>
      ) : null}
    </div>
  )
}
